import React from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from "@mui/material";
import { ISelectOption } from "../../constants/types";
import { ClearIcon } from "@mui/x-date-pickers";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { CustomFormLabel } from "./CustomFormLabel";

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 8;

export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 5 + ITEM_PADDING_TOP,
      width: 250,
    },
    sx: {
      "& .MuiMenuItem-root": {
        minHeight: "32px",
        paddingTop: "4px",
        paddingBottom: "4px",
        fontSize: "0.85rem",
        fontWeight: 600,
        fontFamily: '"Titillium Web", sans-serif',
      },

      "& .MuiCheckbox-root": {
        padding: "4px",
      },
    },
  },
};

export const CustomSelect = styled((props: SelectProps) => (
  <Select {...props} MenuProps={MenuProps} />
))(({ theme }) => ({
  fontFamily: '"Titillium Web", sans-serif',

  "& .MuiSelect-select": {
    padding: "9px 13px",
    fontSize: "0.87rem",
    fontWeight: 600, // ✅ default
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
  },

  /* ---------------- PLACEHOLDER ---------------- */
  "&::placeholder": {
    color: "#767e89",
    opacity: 1,
    fontWeight: 600,
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "5px",
    borderColor:
      theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#757575",
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.text.secondary,
  },

  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: theme.palette.primary.main,
  },

  "&.Mui-disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

interface ICustomSelectProps extends SelectProps {
  options: { label: number | string | null; value: string | number }[];
  helperText?: string;
  readOnlyMode?: boolean;
  optionalData?: ISelectOption[];
  clearable?: boolean;
  onClear?: () => void;
  placeholder?: string;
}

const CustomClearIcon = styled(ClearIcon)(() => ({
  fontSize: "1.2rem",
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const ControlledCustomSelect: React.FC<ICustomSelectProps> = (props) => {
  const renderPlaceholder = () => props.placeholder || "Select One";

  const handleClear = () => {
    if (props.onClear) props.onClear();
  };

  const getOptionValue = () => {
    if (props.multiple) {
      return Array.isArray(props.value) ? props.value : [];
    }
    return props.value || "";
  };

  return (
    <>
      <CustomSelect
        {...props}
        multiple={props.multiple}
        value={getOptionValue()}
        displayEmpty
        renderValue={(selected) => {
          if (!selected || (Array.isArray(selected) && selected.length === 0)) {
            return (
              <Typography sx={{ color: "#767e89" }}>
                {renderPlaceholder()}
              </Typography>
            );
          }
          return getLabelFromValue(selected, props.options);
        }}
        endAdornment={
          props.clearable && props.value ? (
            <InputAdornment position="end" sx={{ marginRight: "7px" }}>
              <ClearButton onClick={handleClear}>
                <CustomClearIcon />
              </ClearButton>
            </InputAdornment>
          ) : null
        }
      >
        {(props.options || []).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {props.multiple && (
              <Checkbox
                checked={
                  Array.isArray(props.value) &&
                  props.value.includes(option.value)
                }
              />
            )}
            {option.label}
          </MenuItem>
        ))}
      </CustomSelect>

      {props.helperText && <ErrorMessage value={props.helperText} />}
    </>
  );
};

const getLabelFromValue = (
  value: any,
  options: ICustomSelectProps["options"],
) => {
  if (Array.isArray(value)) {
    const data = options.filter((option) => value.includes(option.value));

    // If more than 2 items are selected, show only first 2 and add "+X" indicator
    if (data.length > 2) {
      const visibleItems = data.slice(0, 2);
      const remainingCount = data.length - 2;

      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          {visibleItems.map((x) => (
            <Chip key={x.value} label={x.label} size="small" />
          ))}
          <Chip
            label={`+${remainingCount}`}
            size="small"
            sx={{
              backgroundColor: "#e0e0e0",
              color: "#666",
              fontWeight: "bold",
            }}
          />
        </Box>
      );
    }

    // If 2 or fewer items, show all
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {data.map((x) => (
          <Chip key={x.value} label={x.label} size="small" />
        ))}
      </Box>
    );
  }

  const option = options.find((option) => option.value === value);
  return option ? option.label : "";
};

interface ICustomSelectorProps extends Omit<SelectProps, "error"> {
  label?: string;
  options: string[] | number[];
  error?: string;
  readOnlyMode?: boolean;
}

export const CustomSelectorWithLabel: React.FC<ICustomSelectorProps> = (
  props,
) => {
  const { error, label, readOnlyMode, ...restProps } = props;
  return (
    <>
      {label && <CustomFormLabel>{label}</CustomFormLabel>}
      {props.readOnlyMode ? (
        <Typography variant="body1" padding={1} fontSize={"0.8rem"}>
          <>{props.value || "--"}</>
        </Typography>
      ) : (
        <CustomSelect
          {...restProps}
          className={props.name}
          value={props.value}
          placeholder={props.placeholder}
        >
          {props.options.map((option) => {
            return (
              <MenuItem key={option} value={option}>
                {/* {props.multiple && Array.isArray(props.value) && (
                <Checkbox checked={props.options.indexOf(option) > -1} />
              )} */}
                {option}
              </MenuItem>
            );
          })}
        </CustomSelect>
      )}
      {error && (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.75rem",
            color: "#e46a76",
            marginLeft: 2,
            marginTop: 0.75,
          }}
        >
          {error}
        </Typography>
      )}
    </>
  );
};
